import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Plus, AlertTriangle, Loader2, Package } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { MaterialGroupedPicker } from './MaterialGroupedPicker';

// Re-export the grouped picker as the default picker for all new usage.
// Pass grouped={false} to opt back into the flat search (legacy/bulk import).
export { MaterialGroupedPicker };

const UOM_OPTIONS = ['Nos', 'MT', 'Kg', 'Bag', 'Ltr', 'Sqm', 'Cum', 'Rmt', 'Roll', 'Set', 'Pair', 'Box', 'Coil', 'Drum'];

/**
 * Shared Material Master picker for all procurement forms (MPR / RFQ / PO).
 * Free-text material entry is no longer allowed — every line must reference an
 * active material_master record. Supports search-as-you-type and an inline
 * "Add New" flow (with a fuzzy-match warning) so a genuinely missing material
 * is added to the master rather than typed loose into the form.
 *
 * Props:
 *   value        — currently selected material_id (master UUID id)
 *   displayName  — name to show for the current value (edit/prefill mode)
 *   onSelect(m)  — called with the full material object (or null on clear)
 *   placeholder, testid, compact
 */
// Smart wrapper: routes to grouped (2-step) or flat picker based on `grouped` prop
export const MaterialPicker = (props) => {
  const { grouped = true, ...rest } = props;
  if (grouped) return <MaterialGroupedPicker {...rest} />;
  return <FlatMaterialPicker {...rest} />;
};

// Original flat search picker (used in bulk import wizard + legacy forms)
const FlatMaterialPicker = ({
  value,
  displayName,
  onSelect,
  placeholder = 'Search Material…',
  testid = 'material-picker',
  compact = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(value ? { id: value, name: displayName } : null);
  const [adding, setAdding] = useState(false);
  const [cats, setCats] = useState([]);
  const [newMat, setNewMat] = useState({ name: '', category: '', hsn_code: '', uom: 'Nos' });
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value) setSelected(null);
    else setSelected((prev) => (prev && prev.id === value ? prev : { id: value, name: displayName }));
  }, [value, displayName]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setAdding(false); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const d = await apiGet('/material-master/search', { q, limit: 20 });
        setResults(d?.items || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  const choose = (m) => {
    setSelected(m);
    setOpen(false); setAdding(false); setQ('');
    onSelect?.(m);
  };

  const clear = () => { setSelected(null); onSelect?.(null); };

  const openAdd = async () => {
    setNewMat({ name: q.trim(), category: '', hsn_code: '', uom: 'Nos' });
    setAdding(true);
    if (cats.length === 0) {
      try {
        const d = await apiGet('/material-master/categories');
        setCats((d?.categories || []).map((c) => c.category).filter(Boolean));
      } catch { /* non-fatal */ }
    }
  };

  const saveNew = async () => {
    const name = newMat.name.trim();
    const hsn = newMat.hsn_code.trim();
    if (!name) { toast.error('Material name is required'); return; }
    if (!/^\d{4}(\d{2}){0,2}$/.test(hsn)) { toast.error('HSN code must be 4, 6 or 8 digits'); return; }
    if (!newMat.category.trim()) { toast.error('Category is required'); return; }
    if (!newMat.uom.trim()) { toast.error('UoM is required'); return; }
    setSaving(true);
    try {
      const created = await apiPost('/material-master', {
        name, hsn_code: hsn, category: newMat.category.trim(), uom: newMat.uom.trim(),
      });
      toast.success(`Added "${created.name}" to Material Master`);
      choose(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add material');
    } finally { setSaving(false); }
  };

  const exactExists = results.some((r) => (r.name || '').trim().toLowerCase() === q.trim().toLowerCase());
  const inputCls = `w-full ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} border border-slate-300 rounded-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none`;

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      {selected && selected.id ? (
        <div className={`flex items-center justify-between gap-2 ${compact ? 'px-2 py-1' : 'px-3 py-2'} border border-emerald-300 bg-emerald-50/60 rounded-sm`} data-testid={`${testid}-selected`}>
          <div className="min-w-0">
            <div className={`font-display font-semibold text-slate-800 truncate ${compact ? 'text-xs' : 'text-sm'}`} title={selected.name || ''}>{selected.name || 'Selected material'}</div>
            {(selected.material_id || selected.uom) && (
              <div className="text-[10px] text-slate-500 truncate">
                {selected.material_id ? <span className="font-mono">{selected.material_id}</span> : null}
                {selected.uom ? ` · ${selected.uom}` : ''}{selected.category ? ` · ${selected.category}` : ''}
              </div>
            )}
          </div>
          <button type="button" onClick={clear} data-testid={`${testid}-clear`} className="shrink-0 text-slate-400 hover:text-rose-600"><X size={compact ? 13 : 15} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => { setOpen(true); }} data-testid={`${testid}-open`}
          className={`flex items-center gap-2 ${inputCls} bg-white text-left text-slate-400`}>
          <Search size={compact ? 13 : 15} className="shrink-0" />
          <span className="truncate">{placeholder}</span>
        </button>
      )}

      {open && !selected?.id && (
        <div className="absolute z-[70] mt-1 w-full min-w-[16rem] bg-white border border-slate-200 rounded-sm shadow-xl" data-testid={`${testid}-dropdown`}>
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-2 px-2 py-1.5 border border-slate-300 rounded-sm focus-within:border-[#3b4872]">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type material name / code…"
                className="w-full text-sm outline-none" data-testid={`${testid}-search-input`} />
              {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
            </div>
          </div>

          {!adding && (
            <div className="max-h-64 overflow-y-auto">
              {results.length === 0 && !loading && (
                <div className="px-3 py-6 text-center text-xs text-slate-400">{q ? 'No matches in Material Master' : 'Start typing to search'}</div>
              )}
              {results.map((m) => (
                <button type="button" key={m.id} onClick={() => choose(m)} data-testid={`${testid}-option`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                  <div className="text-sm font-display font-semibold text-slate-800 flex items-center gap-1.5"><Package size={12} className="text-[#3b4872] shrink-0" />{m.name}</div>
                  <div className="text-[10px] text-slate-500 ml-[18px]">
                    {m.material_id ? <span className="font-mono">{m.material_id}</span> : null}
                    {m.uom ? ` · ${m.uom}` : ''}{m.category ? ` · ${m.category}` : ''}
                  </div>
                </button>
              ))}
              {q.trim() && !exactExists && (
                <button type="button" onClick={openAdd} data-testid={`${testid}-add-new`}
                  className="w-full text-left px-3 py-2.5 bg-amber-50/70 hover:bg-amber-100/70 border-t border-amber-200 text-[#3b4872] font-display font-semibold text-sm flex items-center gap-2">
                  <Plus size={14} /> Add “{q.trim()}” to Material Master
                </button>
              )}
            </div>
          )}

          {adding && (
            <div className="p-3 space-y-2.5" data-testid={`${testid}-add-form`}>
              {results.length > 0 && (
                <div className="flex items-start gap-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-sm" data-testid={`${testid}-fuzzy-warning`}>
                  <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-amber-800">
                    Looks similar to: <span className="font-semibold">{results.slice(0, 2).map((r) => r.name).join(', ')}</span>. Pick the existing one above if it’s the same material.
                  </div>
                </div>
              )}
              <Field label="Material Name *" value={newMat.name} onChange={(v) => setNewMat({ ...newMat, name: v })} testid={`${testid}-new-name`} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Category *</FieldLabel>
                  <input list={`${testid}-cats`} value={newMat.category} onChange={(e) => setNewMat({ ...newMat, category: e.target.value })}
                    className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-sm focus:border-[#3b4872] focus:outline-none" data-testid={`${testid}-new-category`} />
                  <datalist id={`${testid}-cats`}>{cats.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <Field label="HSN Code *" value={newMat.hsn_code} onChange={(v) => setNewMat({ ...newMat, hsn_code: v.replace(/[^0-9]/g, '') })} placeholder="4 / 6 / 8 digits" testid={`${testid}-new-hsn`} />
              </div>
              <div>
                <FieldLabel>Unit of Measure *</FieldLabel>
                <input list={`${testid}-uoms`} value={newMat.uom} onChange={(e) => setNewMat({ ...newMat, uom: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-sm focus:border-[#3b4872] focus:outline-none" data-testid={`${testid}-new-uom`} />
                <datalist id={`${testid}-uoms`}>{UOM_OPTIONS.map((u) => <option key={u} value={u} />)}</datalist>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setAdding(false)} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50">Back</button>
                <button type="button" onClick={saveNew} disabled={saving} data-testid={`${testid}-new-save`}
                  className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1.5 disabled:opacity-50">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add & Select
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FieldLabel = ({ children }) => (
  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{children}</label>
);

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-sm focus:border-[#3b4872] focus:outline-none" data-testid={testid} />
  </div>
);

export default MaterialPicker;
